import {
  DEFAULT_THEME_PREFERENCE,
  STORAGE_KEYS,
  type StorageService,
  type ThemePreference,
  type ThemeService,
} from './types';
import { createMemoryStorageService } from './storage';

/**
 * The theme, and the one signal that expresses it (ADR-0017, ADR-0016).
 *
 * ═══ THE CONTRACT WITH `index.html` ═══
 *
 * The theme has to be on the document before first paint, so an inline script in `<head>`
 * stamps it; that script's bytes are hashed into the CSP in `nginx.conf`, and
 * `scripts/theme-bootstrap.mjs` owns both copies (`npm run check:theme`). This module is
 * the second half of that contract and must agree with it exactly:
 *
 *   storage key      `jsonfmt.theme`
 *   stored value     JSON-encoded `'light' | 'dark' | 'system'` — a bare, unquoted value is
 *                    tolerated too, so a hand-edited devtools entry behaves the same in
 *                    both halves instead of theming first paint and nothing after it
 *   resolution       `'system'` → `matchMedia('(prefers-color-scheme: dark)')`, live
 *   the ONE signal   `document.documentElement.dataset.theme = 'light' | 'dark'`
 *
 * `theme.bootstrap.test.ts` executes the actual bytes out of `index.html` against jsdom and
 * compares the result with this service over the full matrix of stored value × OS setting.
 * That is what keeps "byte-for-byte" a fact rather than a comment.
 *
 * ═══ WHAT THIS FILE DELIBERATELY DOES NOT DO ═══
 *
 * It does not set `style.colorScheme`. `tokens.css` owns `color-scheme` (ADR-0017), and two
 * writers for one property is how a `data-theme` toggle ends up with browser-painted
 * scrollbars and form controls from the other theme. One owner per property.
 *
 * It also does not write `'system'` onto the element. `dataset.theme` carries the RESOLVED
 * value, never the preference: CSS cannot resolve `system`, and `tokens.css` matches on
 * `[data-theme='dark']`.
 */

/** `'system'` is a preference; it is never a resolved theme. */
export type ResolvedTheme = 'light' | 'dark';

/**
 * Named here because this module is the second half of the `index.html` contract above and a
 * reader of it should see the key. DERIVED, not re-declared: `STORAGE_KEYS` in `./types` is
 * the one declaration every layer can import (see its header).
 */
export const THEME_STORAGE_KEY = STORAGE_KEYS.theme;

/** The media query both halves of the contract use. One string, spelled once. */
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Validator for `StorageService.get`. Anything that is not one of the three words is `null`
 * — which is how a hand-edited `jsonfmt.theme` becomes "fall back to `system`" rather than
 * a crash or an invalid `data-theme`.
 */
export function parseThemePreference(raw: unknown): ThemePreference | null {
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : null;
}

/** The part of `MediaQueryList` this service uses, with both listener vintages optional. */
export interface MediaQueryLike {
  readonly matches: boolean;
  /** Modern browsers, and Safari ≥ 14. */
  addEventListener?(type: 'change', listener: () => void): void;
  removeEventListener?(type: 'change', listener: () => void): void;
  /** Deprecated, and the only one Safari 13 has. */
  addListener?(listener: () => void): void;
  removeListener?(listener: () => void): void;
}

/** The one element that carries `data-theme`. Narrowed to what we write. */
export interface ThemeRoot {
  readonly dataset: { theme?: string | undefined };
}

export interface ThemeDeps {
  readonly storage: StorageService;
  /** Defaults to `document.documentElement`; `null` means "do not touch any DOM". */
  readonly root?: ThemeRoot | null;
  /** Defaults to `matchMedia(DARK_MEDIA_QUERY)`; `null` means "no OS signal, assume light". */
  readonly media?: MediaQueryLike | null;
}

/** `matchMedia` if the host has it, `null` otherwise (jsdom without the shim, a worker). */
function ambientMedia(): MediaQueryLike | null {
  try {
    const matchMedia = (globalThis as { matchMedia?: (q: string) => MediaQueryLike }).matchMedia;
    return typeof matchMedia === 'function' ? matchMedia(DARK_MEDIA_QUERY) : null;
  } catch {
    return null;
  }
}

function ambientRoot(): ThemeRoot | null {
  try {
    const doc = (globalThis as { document?: { documentElement?: ThemeRoot } }).document;
    return doc?.documentElement ?? null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to the media query across both listener vintages, returning an unsubscribe.
 *
 * Wrapped: a host object that has the property but throws on use (some embedded webviews)
 * must degrade to "no live OS updates", not to a blank application.
 */
function watchMedia(media: MediaQueryLike, onChange: () => void): () => void {
  try {
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onChange);
      return () => {
        try {
          media.removeEventListener?.('change', onChange);
        } catch {
          /* nothing useful to do while tearing down */
        }
      };
    }
    if (typeof media.addListener === 'function') {
      media.addListener(onChange);
      return () => {
        try {
          media.removeListener?.(onChange);
        } catch {
          /* as above */
        }
      };
    }
  } catch {
    /* fall through to the no-op */
  }
  return () => {};
}

/**
 * The service. Built once, from `createServices()`.
 *
 * The extra `dispose()` is not part of `ThemeService` — nothing in the application tears the
 * theme down — but a test that builds a hundred of these against one fake media object
 * would otherwise leave a hundred listeners on it. `Services.theme` is typed as
 * `ThemeService`, so the extra member is invisible to production callers.
 */
export function createThemeService(deps: ThemeDeps): ThemeService & { dispose(): void } {
  const { storage } = deps;
  const root = deps.root !== undefined ? deps.root : ambientRoot();
  const media = deps.media !== undefined ? deps.media : ambientMedia();

  const listeners = new Set<() => void>();

  let preference: ThemePreference = storage.get(
    THEME_STORAGE_KEY,
    parseThemePreference,
    DEFAULT_THEME_PREFERENCE,
  );
  let resolved: ResolvedTheme = resolve(preference);

  function resolve(p: ThemePreference): ResolvedTheme {
    if (p !== 'system') return p;
    try {
      return media?.matches === true ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }

  function stamp(value: ResolvedTheme): void {
    if (root === null) return;
    try {
      root.dataset.theme = value;
    } catch {
      /* a detached or frozen element is not worth a crash */
    }
  }

  function notify(): void {
    // Copied first: a listener is allowed to unsubscribe itself from inside the callback.
    for (const listener of [...listeners]) listener();
  }

  /**
   * Recompute and publish. Notifies when the PREFERENCE or the RESOLVED theme moved.
   *
   * Not "notify on every event": the OS flipping to dark while the user has pinned `light`
   * changes nothing on screen, and a re-render there is a lie about state having changed.
   */
  function settle(next: ThemePreference): void {
    const nextResolved = resolve(next);
    const changed = next !== preference || nextResolved !== resolved;
    preference = next;
    resolved = nextResolved;
    stamp(resolved);
    if (changed) notify();
  }

  // First paint already stamped this (the inline bootstrap did), and it re-stamps here on
  // purpose: it is idempotent, and it makes the element right even when the bootstrap was
  // stripped — an embedded build, a test, a browser extension that ate the inline script.
  stamp(resolved);

  const unwatch =
    media === null
      ? () => {}
      : watchMedia(media, () => {
          settle(preference);
        });

  return {
    getPreference: () => preference,
    getResolved: () => resolved,

    setPreference(next: ThemePreference): void {
      storage.set(THEME_STORAGE_KEY, next);
      settle(next);
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    dispose(): void {
      unwatch();
      listeners.clear();
    },
  };
}

/**
 * A theme service with no DOM and no OS signal: memory preference, always resolving light
 * unless the preference says otherwise.
 *
 * This is what `createNullServices()` installs, and it is the reason a command test runs in
 * the bare-Node `logic` project (ADR-0019): nothing here reaches for `document` or
 * `matchMedia` even to feature-detect them.
 */
export function createDetachedThemeService(
  initial: ThemePreference = 'system',
): ThemeService & { dispose(): void } {
  return createThemeService({
    storage: createMemoryStorageService({ [THEME_STORAGE_KEY]: JSON.stringify(initial) }),
    root: null,
    media: null,
  });
}
